import { useState, useCallback, useEffect, useRef } from 'react'
import { StorePerformanceItem } from '../types'
import { ScheduleService } from '../../../../../../../service/schedule'
import { httpFetch } from '../../../../../../../utils/crud'
import { buildQueryParams, getCompanyId } from '../utils/buildQueryParams'
import { POLL_CONFIG } from '../constants'

interface UseDownloadReportProps {
  canConsult: boolean
  hasQueried: boolean
  hasAssignedCompany: boolean
  assignedCompany: { id: number | string } | null | undefined
  selectedCompany: StorePerformanceItem | null
  selectedUnits: StorePerformanceItem[]
  selectedQuestionnaires: StorePerformanceItem[]
  appliedPeriodFrom: Date | null
  appliedPeriodTo: Date | null
  appliedPeriodYear: number
  appliedPeriodWeek: string[]
  filterDivision: StorePerformanceItem[]
  filterZone: StorePerformanceItem[]
  filterUser: StorePerformanceItem[]
  filterRole: StorePerformanceItem[]
  selectedVersionsByQuestionnaire: Record<string, string[]>
  appliedCriticalRange: [number, number]
  showSnackbar: (message: string, type?: 'success' | 'error' | 'warning' | 'info', interpolationOptions?: Record<string, unknown>) => void
}

interface UseDownloadReportReturn {
  downloadPopoverOpen: boolean
  downloadPopoverAnchor: HTMLElement | null
  downloadingPdf: boolean
  downloadingExcel: boolean
  handleDownload: (event: React.MouseEvent<HTMLElement>) => void
  closeDownloadPopover: () => void
  handleDownloadPdf: () => void
  handleDownloadExcel: () => void
}

const useDownloadReport = ({
  canConsult,
  hasQueried,
  hasAssignedCompany,
  assignedCompany,
  selectedCompany,
  selectedUnits,
  selectedQuestionnaires,
  appliedPeriodFrom,
  appliedPeriodTo,
  appliedPeriodYear,
  appliedPeriodWeek,
  filterDivision,
  filterZone,
  filterUser,
  filterRole,
  selectedVersionsByQuestionnaire,
  appliedCriticalRange,
  showSnackbar,
}: UseDownloadReportProps): UseDownloadReportReturn => {
  const [downloadPopoverOpen, setDownloadPopoverOpen] = useState(false)
  const [downloadPopoverAnchor, setDownloadPopoverAnchor] = useState<HTMLElement | null>(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadingExcel, setDownloadingExcel] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [])

  const closeDownloadPopover = useCallback(() => {
    setDownloadPopoverOpen(false)
  }, [])

  const handleDownload = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDownloadPopoverAnchor(event.currentTarget)
    setDownloadPopoverOpen(true)
  }, [])

  const downloadFile = useCallback(async (type: 'pdf' | 'excel') => {
    if (!canConsult || !hasQueried) return

    const setLoading = type === 'pdf' ? setDownloadingPdf : setDownloadingExcel
    setLoading(true)
    closeDownloadPopover()

    try {
      showSnackbar('general.waitAMoment', 'info')

      const companyId = getCompanyId(hasAssignedCompany, assignedCompany, selectedCompany)
      const baseParams = buildQueryParams({
        companyId,
        selectedUnits,
        selectedQuestionnaires,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedPeriodYear,
        appliedPeriodWeek,
        filterDivision,
        filterZone,
        filterUser,
        filterRole,
        selectedVersionsByQuestionnaire,
      })

      const generateParams = {
        ...baseParams,
        order: 'ASC' as const,
        range_critical_init: appliedCriticalRange[0],
        range_critical_end: appliedCriticalRange[1],
        type,
      }

      const generateResponse = await ScheduleService(httpFetch).generateFileStoreScheduled(generateParams)

      if (generateResponse.code !== '00' || !generateResponse.uuid) {
        showSnackbar('dashboard.storePerformance.error.generateFailed', 'error')
        setLoading(false)
        return
      }

      const uuid = generateResponse.uuid
      let attempts = 0

      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }

      const finish = (success: boolean) => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        setLoading(false)
        showSnackbar(
          success ? 'dashboard.storePerformance.downloadSuccess' : 'dashboard.storePerformance.error.downloadFailed',
          success ? 'success' : 'error'
        )
      }

      pollingRef.current = setInterval(async () => {
        attempts++
        try {
          const reportResponse = await ScheduleService(httpFetch).getGeneratedReportStoreScheduled(uuid)

          if (reportResponse.reportUrl) {
            window.open(reportResponse.reportUrl, '_blank')
            finish(true)
          } else if (attempts >= POLL_CONFIG.MAX_ATTEMPTS) {
            finish(false)
          }
        } catch {
          if (attempts >= POLL_CONFIG.MAX_ATTEMPTS) {
            finish(false)
          }
        }
      }, POLL_CONFIG.INTERVAL_MS)
    } catch {
      showSnackbar('dashboard.storePerformance.error.downloadFailed', 'error')
      setLoading(false)
    }
  }, [canConsult, hasQueried, appliedPeriodFrom, appliedPeriodTo, appliedPeriodWeek, appliedPeriodYear, selectedUnits, selectedQuestionnaires, selectedVersionsByQuestionnaire, hasAssignedCompany, assignedCompany, selectedCompany, filterDivision, filterZone, filterUser, filterRole, appliedCriticalRange, showSnackbar, closeDownloadPopover])

  const handleDownloadPdf = useCallback(() => {
    downloadFile('pdf')
  }, [downloadFile])

  const handleDownloadExcel = useCallback(() => {
    downloadFile('excel')
  }, [downloadFile])

  return {
    downloadPopoverOpen,
    downloadPopoverAnchor,
    downloadingPdf,
    downloadingExcel,
    handleDownload,
    closeDownloadPopover,
    handleDownloadPdf,
    handleDownloadExcel,
  }
}

export { useDownloadReport }
